import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import toastr from "../../../utils/toast";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NEW = "__new__";

// "Swiggy", "SWIGGY" and "Swi ggy" are the same company; PINE often holds one
// company as several rows spelled like that.
const sameName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\\/g, "")
    .replace(/\s+/g, "");

/**
 * Add a company to a role's search map.
 *
 * The company is picked from PINE's own companies, so candidates at it count
 * towards it. Picking one ticks every PINE row with the same name - Swiggy is
 * six rows - and the consultant can untick any that are a different company.
 * Nothing ticked adds it by name only; it stays white until linked.
 *
 * @param {object}   props
 * @param {number}   props.roleId
 * @param {Array}    props.columns        the map's columns, for the industry list
 * @param {object}   [props.presetColumn] opened from a column's "+ Add"
 * @param {string}   props.token
 * @param {Function} props.onClose
 * @param {Function} props.onAdded        called after a successful add
 */
export default function SearchMapAddCompany({
  roleId,
  columns,
  presetColumn,
  token,
  onClose,
  onAdded,
}) {
  // "col:<id>" an existing column, "pine:<name>" a PINE industry not yet on
  // the map, NEW a name typed in.
  const firstColumn = presetColumn ?? columns[0];
  const [columnChoice, setColumnChoice] = useState(
    firstColumn ? `col:${firstColumn.id}` : NEW,
  );
  const [newIndustry, setNewIndustry] = useState("");

  // PINE's current industries; the ones already on the map are listed once,
  // under "On this map".
  const [pineIndustries, setPineIndustries] = useState([]);
  useEffect(() => {
    axios
      .get(`${REACT_APP_API_BASE_URL}/workstation/search_map/industries`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPineIndustries(res.data?.industries || []))
      .catch(() => setPineIndustries([]));
  }, [token]);
  const onMap = new Set(columns.map((c) => c.industry.trim().toLowerCase()));
  const notOnMap = pineIndustries.filter(
    (name) => !onMap.has(name.trim().toLowerCase()),
  );
  const [name, setName] = useState("");
  const [link, setLink] = useState("");

  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ticked, setTicked] = useState(new Set());
  // Set when a result is picked: the next results tick every row with this name.
  const [pickTarget, setPickTarget] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Only the newest search's answer is used; a slower older one is dropped.
  const latestQuery = useRef("");

  useEffect(() => {
    const q = name.trim();
    latestQuery.current = q;
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${REACT_APP_API_BASE_URL}/workstation/search_map/company_search`,
          {
            params: { q },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (latestQuery.current !== q) return;
        const companies = res.data?.companies || [];
        setResults(companies);
        if (pickTarget) {
          setTicked(
            new Set(
              companies
                .filter((c) => sameName(c.name) === pickTarget)
                .map((c) => c.id),
            ),
          );
        }
      } catch (err) {
        if (latestQuery.current === q) setResults([]);
      } finally {
        if (latestQuery.current === q) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [name, pickTarget, token]);

  // Typing again starts over: nothing is linked until a result is picked.
  const typeName = (value) => {
    setName(value);
    setPickTarget(null);
    setTicked(new Set());
    setError("");
  };

  // Searching the picked name brings back all its rows, not just the ones
  // that made the first 20 for what was typed.
  const pick = (company) => {
    setName(company.name);
    setPickTarget(sameName(company.name));
    setTicked(new Set([company.id]));
    if (!link.trim()) setLink(company.linkedin_url || company.website || "");
    setError("");
  };

  const toggle = (id) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isNew = columnChoice === NEW;
  const columnId = columnChoice.startsWith("col:")
    ? Number(columnChoice.slice(4))
    : null;
  const industry = isNew
    ? newIndustry.trim()
    : columnId
      ? columns.find((c) => c.id === columnId)?.industry
      : columnChoice.slice(5);
  const canAdd = !!industry && !!name.trim() && !saving;

  const submit = async () => {
    if (!canAdd) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${REACT_APP_API_BASE_URL}/workstation/search_map/${roleId}/company`,
        {
          ...(columnId ? { column_id: columnId } : { new_industry: industry }),
          name: name.trim(),
          link: link.trim(),
          companyinfo_ids: [...ticked],
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastr.success(
        ticked.size > 0
          ? `${name.trim()} added to ${industry}.`
          : `${name.trim()} added to ${industry}, by name only. It stays white until it's linked to a PINE company.`,
      );
      onAdded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Could not add the company.");
    } finally {
      setSaving(false);
    }
  };

  const q = name.trim();

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h4 className="modal-title">Add a company to the search map</h4>
                <p className="text-muted small mb-0">
                  Pick it from PINE so the candidates there count towards it.
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">Industry</label>
                <select
                  className="form-select"
                  value={columnChoice}
                  onChange={(e) => setColumnChoice(e.target.value)}
                >
                  {columns.length > 0 && (
                    <optgroup label="On this map">
                      {columns.map((c) => (
                        <option key={c.id} value={`col:${c.id}`}>
                          {c.industry}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {notOnMap.length > 0 && (
                    <optgroup label="All PINE industries">
                      {notOnMap.map((name) => (
                        <option key={name} value={`pine:${name}`}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <option value={NEW}>+ Other (type a name)…</option>
                </select>
                {isNew && (
                  <input
                    autoFocus
                    className="form-control mt-2"
                    placeholder="New industry name (e.g. Fintech)"
                    maxLength={120}
                    value={newIndustry}
                    onChange={(e) => setNewIndustry(e.target.value)}
                  />
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Company name</label>
                <input
                  autoFocus={!isNew}
                  className="form-control"
                  placeholder="Start typing, e.g. Swiggy"
                  maxLength={255}
                  value={name}
                  onChange={(e) => typeName(e.target.value)}
                />

                {q.length >= 2 && (
                  <div
                    className="border rounded mt-2 overflow-auto"
                    style={{ maxHeight: 240 }}
                  >
                    {searching && results.length === 0 && (
                      <div className="text-muted small px-3 py-2">
                        Searching PINE…
                      </div>
                    )}
                    {!searching && results.length === 0 && (
                      <div className="text-muted small px-3 py-2">
                        No PINE company starts with “{q}”. You can still add it
                        by name — it stays white until it is linked.
                      </div>
                    )}
                    {results.map((c) => (
                      <label
                        key={c.id}
                        className="d-flex align-items-center gap-2 px-3 py-1 border-bottom small mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={ticked.has(c.id)}
                          onChange={() =>
                            ticked.size === 0 ? pick(c) : toggle(c.id)
                          }
                        />
                        <span className="flex-grow-1">
                          <span className="fw-medium">{c.name}</span>
                          {(c.linkedin_url || c.website) && (
                            <span className="text-muted ms-2">
                              {c.linkedin_url ? "LinkedIn" : c.website}
                            </span>
                          )}
                        </span>
                        <span className="text-muted text-nowrap">
                          <i className="fa-solid fa-user me-1"></i>
                          {c.candidates}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {q.length >= 2 && (
                  <div className="form-text">
                    {ticked.size > 0
                      ? `Linked to ${ticked.size} PINE ${
                          ticked.size === 1 ? "company" : "companies"
                        } — untick any that are a different company.`
                      : "Not linked to PINE yet — tick the matching company, or add it by name only."}
                  </div>
                )}
              </div>

              <div className="mb-1">
                <label className="form-label fw-semibold">
                  Company LinkedIn URL or domain{" "}
                  <span className="text-muted fw-normal">(optional)</span>
                </label>
                <input
                  className="form-control"
                  placeholder="e.g. linkedin.com/company/zepto or zeptonow.com"
                  maxLength={255}
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
                <div className="form-text">Shown as a link on the map.</div>
              </div>

              {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center"
                onClick={submit}
                disabled={!canAdd}
              >
                <i className="fa-solid fa-plus me-1"></i>
                {saving ? "Adding…" : "Add to map"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
